import { createContext, useContext, useState, ReactNode } from "react";

interface StepNavigationContextType {
  currentStep: number;
  candidateSteps: Record<string, number>; // Track step per candidate email
  setCurrentStep: (step: number) => void;
  setCandidateStep: (candidateEmail: string, step: number) => void;
  getCandidateStep: (candidateEmail: string) => number;
  canNavigateToStep: (step: number) => boolean;
}

const StepNavigationContext = createContext<StepNavigationContextType | undefined>(undefined);

export function StepNavigationProvider({ children }: { children: ReactNode }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [candidateSteps, setCandidateSteps] = useState<Record<string, number>>({});

  const setCandidateStep = (candidateEmail: string, step: number) => {
    setCandidateSteps(prev => ({
      ...prev,
      [candidateEmail]: step
    }));
  };

  const getCandidateStep = (candidateEmail: string): number => {
    return candidateSteps[candidateEmail] || 1;
  };

  const canNavigateToStep = (step: number): boolean => {
    // For now, allow navigation to any step
    // You can add validation logic here if needed
    return step >= 1 && step <= 4;
  };

  return (
    <StepNavigationContext.Provider
      value={{
        currentStep,
        candidateSteps,
        setCurrentStep,
        setCandidateStep,
        getCandidateStep,
        canNavigateToStep,
      }}
    >
      {children}
    </StepNavigationContext.Provider>
  );
}

export function useStepNavigation() {
  const context = useContext(StepNavigationContext);
  if (!context) {
    throw new Error("useStepNavigation must be used within a StepNavigationProvider");
  }
  return context;
}